import React, { useState, memo } from 'react';
import { 
  Calendar,
  Clock,
  MapPin,
  Edit2,
  Trash2,
  Copy,
  Loader2,
  Plus,
  Target,
  Users,
  Star
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { GamePlan, Team } from '../../types';
import { sortGamePlans, isTodayGame, isNextGame, getGamePlanStatus, getGamePlanTitle } from '../../utils/gamePlanUtils';
import { formatDate } from '../../utils/dateTime';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import UpgradeModal from '../UpgradeModal';

type GamePlanWithAnalysis = GamePlan;

interface GamePlanListProps {
  gamePlans: GamePlanWithAnalysis[];
  selectedGamePlanId: string | null;
  onGamePlanSelect: (gamePlan: GamePlanWithAnalysis) => void;
  onEditGamePlan: (gamePlan: GamePlanWithAnalysis) => void;
  onDeleteGamePlan: (gamePlanId: string) => void;
  onAddGamePlan: () => void;
  isMobileView: boolean;
  currentTeam: Team | null;
  mprPlayerCount: number;
  onRefresh?: () => void;
}

const GamePlanList = memo(function GamePlanList({
  gamePlans,
  selectedGamePlanId,
  onGamePlanSelect,
  onEditGamePlan,
  onDeleteGamePlan,
  onAddGamePlan,
  isMobileView,
  currentTeam,
  mprPlayerCount,
  onRefresh
}: GamePlanListProps) {
  const [isCloningGamePlan, setIsCloningGamePlan] = useState<string | null>(null);

  // Pro feature access for clone game plan
  const { checkFeatureAccess, showUpgradeModal, handleUpgrade, handleCloseModal, isProUser } = useFeatureAccess();

  // Sort game plans with smart ordering (upcoming first, then past)
  const sortedGamePlans = sortGamePlans(gamePlans);

  // Clone game plan functionality
  const handleCloneGamePlan = async (gamePlan: GamePlanWithAnalysis) => {
    if (!currentTeam) return;

    // Pro-only feature — block free users and show upgrade modal.
    // Using 'Duplicate schedule' to avoid matching the game plan count-limit
    // branch inside checkFeatureAccess (which checks 'game plan' substring).
    if (!isProUser) {
      checkFeatureAccess('Duplicate schedule');
      return;
    }
    
    try {
      setIsCloningGamePlan(gamePlan.id);
      
      // Clone the game plan
      const { data: newGamePlan, error: gamePlanError } = await supabase
        .from('game_plans')
        .insert({
          team_id: currentTeam.id,
          title: `${gamePlan.title} (Copy)`,
          game_date: gamePlan.game_date,
          offensive_strategy: gamePlan.offensive_strategy,
          defensive_strategy: gamePlan.defensive_strategy,
          special_teams_strategy: gamePlan.special_teams_strategy,
          mpr_substitution_plan: gamePlan.mpr_substitution_plan,
          opponent_name: gamePlan.opponent_name,
          key_matchups: gamePlan.key_matchups,
          special_considerations: gamePlan.special_considerations,
          game_time: gamePlan.game_time,
          location: gamePlan.location,
          status: 'draft'
        })
        .select()
        .single();

      if (gamePlanError) throw gamePlanError;

      toast.success('Game plan copied successfully');
      onRefresh?.();
    } catch (error) {
      console.error('Error copying game plan:', error);
      toast.error('Failed to copy game plan');
    } finally {
      setIsCloningGamePlan(null);
    }
  };

  // Empty state component
  const EmptyState = memo(() => (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="bg-primary bg-opacity-10 rounded-full p-6 mb-4">
        <Target size={48} className="text-primary" />
      </div>
      <h3 className="text-xl font-semibold mb-2">No game plans yet</h3>
      <p className="text-gray-600 mb-6 max-w-md">
        Create your first game plan to start analyzing opponents and strategizing for upcoming games.
      </p>
      <button
        onClick={onAddGamePlan}
        className="btn-primary flex items-center gap-2"
      >
        <Plus size={20} />
        Create Your First Game Plan
      </button>
    </div>
  ));

  if (gamePlans.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <EmptyState />
      </div>
    );
  }

  // Get MPR count for display
  const getMPRPlayerCount = (gamePlan: GamePlanWithAnalysis) => {
    return mprPlayerCount;
  };

  return (
    <div className="relative">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="space-y-4">
          {sortedGamePlans.map((gamePlan, index) => {
            const isToday = isTodayGame(gamePlan);
            const isNext = isNextGame(gamePlan, sortedGamePlans);
            const gamePlanStatus = getGamePlanStatus(gamePlan);
            const isFirstPastGame = index > 0 && 
              getGamePlanStatus(sortedGamePlans[index - 1]).status !== 'past' && 
              gamePlanStatus.status === 'past';
            
            return (
              <React.Fragment key={gamePlan.id}>
                {isFirstPastGame && (
                  <div className="flex items-center gap-2 py-2">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="text-sm text-gray-500 font-medium px-2">Past Games</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                )}
                <div
                  className={`relative p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedGamePlanId === gamePlan.id
                      ? 'border-primary bg-primary bg-opacity-5'
                      : isToday
                      ? 'border-yellow-400 bg-yellow-50 hover:border-yellow-500 shadow-sm'
                      : 'border-gray-200 hover:border-primary hover:bg-primary hover:bg-opacity-5'
                  }`}
                  onClick={() => onGamePlanSelect(gamePlan)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold">
                          {getGamePlanTitle(gamePlan, currentTeam)}
                        </h3>
                        {isNext && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                            <Star size={12} className="fill-yellow-600" />
                            {gamePlanStatus.status === 'today' ? 'Today' : 'Next'}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 text-sm text-gray-600 mt-1">
                        {gamePlan.game_date && (
                          <div className="flex items-center">
                            <Calendar size={16} className="mr-2" />
                            {formatDate(gamePlan.game_date)}
                            <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
                              gamePlanStatus.status === 'today' 
                                ? 'bg-green-100 text-green-800' 
                                : gamePlanStatus.status === 'upcoming'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {gamePlanStatus.label}
                            </span>
                          </div>
                        )}
                        {gamePlan.game_time && (
                          <div className="flex items-center">
                            <Clock size={16} className="mr-2" />
                            {gamePlan.game_time}
                          </div>
                        )}
                       
                        {gamePlan.location && (
                          <div className="flex items-center">
                            <MapPin size={16} className="mr-2" />
                            {gamePlan.location}
                          </div>
                        )}
                        <div className="flex items-center">
                          <Users size={16} className="mr-2" />
                          {getMPRPlayerCount(gamePlan)} MPR players
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloneGamePlan(gamePlan);
                        }}
                        className="p-2 text-gray-600 hover:text-primary rounded-full hover:bg-primary hover:bg-opacity-10 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isProUser ? 'Clone Game Plan' : 'Clone Game Plan (Pro Feature)'}
                        disabled={isCloningGamePlan === gamePlan.id}
                      >
                        {isCloningGamePlan === gamePlan.id ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditGamePlan(gamePlan);
                        }}
                        className="p-2 text-gray-600 hover:text-primary rounded-full hover:bg-primary hover:bg-opacity-10"
                        title="Edit Game Plan"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteGamePlan(gamePlan.id);
                        }}
                        className="p-2 text-gray-600 hover:text-red-600 rounded-full hover:bg-red-50"
                        title="Delete Game Plan"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={handleCloseModal}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
});

export default GamePlanList; 